// src/pages/portal/PortalConocimientoPage.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePortalArticulos, usePortalCategorias } from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Badge,
  Skeleton,
  EmptyState,
} from '@/components/ui';
import { Search, BookOpen, Folder, FileText, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

function ArticulosSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-24" />
      ))}
    </div>
  );
}

export default function PortalConocimientoPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoriaId, setCategoriaId] = useState<string>('');

  const { data: categorias, isLoading: loadingCategorias } = usePortalCategorias();
  const { data: articulosData, isLoading: loadingArticulos } = usePortalArticulos({
    query: searchQuery || undefined,
    categoria_id: categoriaId || undefined,
  });

  const articulos = articulosData?.items || [];

  const tipoBadgeVariant = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'guia':
        return 'info';
      case 'faq':
        return 'warning';
      case 'tutorial':
        return 'success';
      case 'documentacion':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Centro de Ayuda
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Encuentra respuestas a tus preguntas
        </p>
      </div>

      {/* Buscador */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Buscar articulos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Categorias */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Folder className="w-5 h-5" />
                Categorias
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingCategorias ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  <button
                    onClick={() => setCategoriaId('')}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                      !categoriaId
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    )}
                  >
                    Todas las categorias
                  </button>
                  {categorias?.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategoriaId(cat.id)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between',
                        categoriaId === cat.id
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      )}
                    >
                      <span>{cat.nombre}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {cat.articulos_count}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Articulos */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Articulos</span>
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {articulosData?.total || 0} resultados
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingArticulos ? (
                <ArticulosSkeleton />
              ) : articulos.length === 0 ? (
                <EmptyState
                  icon={BookOpen}
                  title="No se encontraron articulos"
                  description={
                    searchQuery
                      ? 'Intenta con otros terminos de busqueda'
                      : 'No hay articulos disponibles en esta categoria'
                  }
                />
              ) : (
                <div className="space-y-3">
                  {articulos.map((articulo) => (
                    <Link
                      key={articulo.id}
                      to={`/portal/conocimiento/${articulo.id}`}
                      className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                          <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              #{articulo.numero}
                            </span>
                            <Badge variant={tipoBadgeVariant(articulo.tipo)}>
                              {articulo.tipo}
                            </Badge>
                            {articulo.categoria && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {articulo.categoria.nombre}
                              </span>
                            )}
                          </div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {articulo.titulo}
                          </h3>
                          {articulo.resumen && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                              {articulo.resumen}
                            </p>
                          )}
                        </div>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
